import { useEffect, useMemo } from 'react';
import createPathMatcher from 'path-match';
import { useAppDispatch } from '@/store/hooks';
import { useLocation, useNavigate, useParams } from './index';
import { Routing } from '@/constants';
import { isAnalyticsChartPage } from '@/search-for-retail/analytics';
import { createModelRoute, editModelRoute, getAreaNameFromPath, getCollectionNameFromPath, getIdFromQueryString, getParamFromQueryString, getRouteIdentifierFromPath, getValidatedModelPath, isHomePage, modelManagerRoute, } from '../lib';
import { useCollectionsSelector } from '@/store/selectors';
import { getBasePath, parseSearch } from '@/utils';
import { switchModelType } from '@/store/slices/sessionSlice';
import { setSessionAreaName, setSessionCollectionName, useSessionAreaName, useSessionCollectionName, useSessionModelType, } from '@/platform-services/session';
export const useShouldDisplayAnalytics = (modelPath) => {
    if (!modelPath)
        return false;
    const isAnalyticsPath = isAnalyticsChartPage(modelPath);
    return isAnalyticsPath;
};
export const usePathname = () => {
    const { pathname } = useLocation();
    return pathname;
};
export const useFormDefaults = () => {
    const { state } = useLocation();
    return state === null || state === void 0 ? void 0 : state.defaultValues;
};
export const useActiveModelPath = () => {
    const pathname = usePathname();
    return getValidatedModelPath(pathname);
};
// this assumes that all plugins begin with the name of the plugin in the url path
export const useActivePluginPath = () => {
    const pathname = usePathname();
    return pathname.split('/')[1];
};
export const useGoToUrl = () => {
    const navigate = useNavigate();
    return (url, state) => {
        void navigate(url, { state });
    };
};
export const getHomePath = () => Routing.MERCHANDISING_PATH;
export const useGoHome = () => {
    const goToUrl = useGoToUrl();
    return (state) => {
        goToUrl(getHomePath(), state);
    };
};
export const useGoToManager = (modelType) => {
    const goToUrl = useGoToUrl();
    const activeCollectionName = useSessionCollectionName();
    const activeAreaName = useSessionAreaName();
    return (state) => {
        goToUrl(modelManagerRoute(modelType, activeCollectionName, activeAreaName), state);
    };
};
export const useGoToEditor = (modelType) => {
    const goToUrl = useGoToUrl();
    const activeCollectionName = useSessionCollectionName();
    const activeAreaName = useSessionAreaName();
    return (modelId, areaName) => {
        goToUrl(editModelRoute(modelType, activeCollectionName, areaName !== null && areaName !== void 0 ? areaName : activeAreaName, String(modelId)));
    };
};
export const useGoToLoginWithNext = () => {
    const navigate = useNavigate();
    return (pathname, search) => {
        void navigate(`${Routing.LOGIN_PATH}?next=${pathname}${search ? encodeURIComponent(search) : ''}`, {
            replace: true,
        });
    };
};
export const useGoToDuplicator = (modelType) => {
    const goToUrl = useGoToUrl();
    const activeCollectionName = useSessionCollectionName();
    const activeAreaName = useSessionAreaName();
    return (defaultValues) => {
        goToUrl(createModelRoute(modelType, activeCollectionName, activeAreaName), {
            defaultValues,
            modelType,
            persistCurrentPage: true,
            action: 'copy',
        });
    };
};
export const useQueryParam = (paramName) => {
    const { search } = useLocation();
    return getParamFromQueryString(search, paramName);
};
export const useIdQueryParam = () => {
    const { search } = useLocation();
    return getIdFromQueryString(search);
};
export const useIsHomePage = () => {
    const pathName = usePathname();
    return isHomePage(pathName);
};
/**
 * Extract collection name from URL pathname with validation
 * @returns Validated collection name from URL or undefined
 */
export const useCollectionNameFromUrl = () => {
    const { pathname } = useLocation();
    const collections = useCollectionsSelector();
    const collectionNames = useMemo(() => collections.map((c) => c.collection), [collections]);
    return getCollectionNameFromPath(pathname, collectionNames);
};
export const useAreaNameFromUrl = () => {
    const { pathname } = useLocation();
    return getAreaNameFromPath(pathname);
};
export const getNextPath = (search) => (parseSearch(search).next || '/').replace('%3F', '?');
export const useNextPath = () => {
    const { search } = useLocation();
    return getNextPath(search);
};
export const useSwitchToOtherArea = () => {
    const goHome = useGoHome();
    return (collectionName, areaName) => {
        setSessionCollectionName(collectionName);
        setSessionAreaName(areaName);
        goHome();
    };
};
export const useGoToNewArea = () => {
    const goToUrl = useGoToUrl();
    const activePath = usePathname();
    const basePath = getBasePath(activePath);
    const activeModelType = useSessionModelType();
    const activeModelPath = getValidatedModelPath(activePath);
    const derivedModelType = activeModelPath ? getRouteIdentifierFromPath(activeModelPath) : undefined;
    const { dashboardTitle } = useParams();
    return (collectionName, areaName) => {
        if (`/${basePath}` === Routing.MERCHANDISING_PATH) {
            const modelType = activeModelType !== null && activeModelType !== void 0 ? activeModelType : derivedModelType;
            if (!modelType) {
                return;
            }
            let url = modelManagerRoute(modelType, collectionName, areaName);
            if (dashboardTitle) {
                url = `${url}/${dashboardTitle}`;
            }
            goToUrl(url);
        }
    };
};
export const useActiveQuery = () => {
    const { search } = useLocation();
    return parseSearch(search);
};
export const useSwitchModelTypeIfNoActiveOne = (modelPath) => {
    const dispatch = useAppDispatch();
    const activeModelType = useSessionModelType();
    useEffect(() => {
        if (!activeModelType && modelPath) {
            const modelType = getRouteIdentifierFromPath(modelPath);
            if (modelType) {
                dispatch(switchModelType(modelType));
            }
        }
    }, [activeModelType, modelPath, dispatch]);
};
export const useIsEditingModel = () => {
    const { pathname, search } = useLocation();
    return !!createPathMatcher({})(Routing.MODEL_MANAGER_PATH)(pathname) && !!parseSearch(search).id;
};
export const useIsCreatingModel = () => {
    const { pathname } = useLocation();
    return !!createPathMatcher({})(Routing.MODEL_CREATOR_PATH)(pathname);
};
export default {
    useShouldDisplayAnalytics,
    useQueryParam,
    useGoToManager,
    useGoToEditor,
    useGoToLoginWithNext,
    useGoToDuplicator,
    useIsHomePage,
    useCollectionNameFromUrl,
    useAreaNameFromUrl,
    useNextPath,
    useGoToNewArea,
    useActiveQuery,
    useIsEditingModel,
    useIsCreatingModel,
};
